import React, { useEffect, useRef, useState } from "react";
import { FiSearch } from "react-icons/fi";
import { BsCalendarDate } from "react-icons/bs";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "../../components/calendartheme.css";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import JSZip from "jszip";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "../../context/AuthContext";
import { affiliateAPI } from "../../services/AffiliatePartnerPortal/affiliateAPI"; // ✅ you need to create this API like networkMemberAPI

const CustomerManagementContent = () => {
  const datePickerRef = useRef(null);
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [selectedAll, setSelectedAll] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [dateRange, setDateRange] = useState([null, null]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const [startDate, endDate] = dateRange;

  // ✅ Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        if (!user?.id) return;
        const response = await affiliateAPI.getByUser(user.phoneNumber);
        const fetched = Array.isArray(response?.data) ? response.data : [];
        setCustomers(fetched);
        setFilteredCustomers(fetched);
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Failed to fetch customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [user?.id]);

  // ✅ Search + Date filter
  useEffect(() => {
    const filtered = (customers || []).filter((c) => {
      if (!c) return false;

      const matchesSearch =
        c?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c?.phone?.toLowerCase().includes(searchTerm.toLowerCase());

      const createdAt = c?.dateAdded ? new Date(c.dateAdded) : null;
      const matchesDate =
        (!startDate || (createdAt && createdAt >= startDate)) &&
        (!endDate || (createdAt && createdAt <= endDate));

      return matchesSearch && matchesDate;
    });

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, startDate, endDate]);

  // ✅ Handle Select All
  const handleSelectAll = () => {
    const newSelectedAll = !selectedAll;
    setSelectedAll(newSelectedAll);

    if (newSelectedAll) {
      setSelectedCustomers(
        filteredCustomers.map((c) => c?._id).filter(Boolean)
      );
    } else {
      setSelectedCustomers([]);
    }
  };

  // ✅ Handle Select Single
  const handleSelectCustomer = (id) => {
    setSelectedCustomers((prev) =>
      prev.includes(id) ? prev.filter((cid) => cid !== id) : [...prev, id]
    );
  };

  // ✅ Export selected or all customers to Excel inside ZIP
  const exportToExcelAndZip = async () => {
    const customersToExport =
      selectedCustomers.length > 0
        ? filteredCustomers.filter((c) => selectedCustomers.includes(c._id))
        : filteredCustomers;

    if (!customersToExport || customersToExport.length === 0) {
      toast.warn("No customers to export.");
      return;
    }

    const wb = XLSX.utils.book_new();
    const dataForExcel = customersToExport.map((c) => ({
      "Full Name": c?.fullName || c?.name || "N/A",
      "Phone Number": c?.phoneNumber || "N/A",
      "WhatsApp Number": c?.whatsappNumber || "N/A",
      Relation: c?.relation || "N/A",
      "Date Added": c?.createdAt
        ? new Date(c.createdAt).toLocaleDateString()
        : "N/A",
      City: c?.permanentAddress?.city || "N/A",
    }));

    const ws = XLSX.utils.json_to_sheet(dataForExcel);
    XLSX.utils.book_append_sheet(wb, ws, "Customers");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const zip = new JSZip();
    zip.file("customers.xlsx", excelBuffer);

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.zip";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ✅ Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setIsDatePickerOpen(false);
      }
    };

    if (isDatePickerOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDatePickerOpen]);

  return (
    <div className="text-white">
      {/* Header */}
      <div className="flex flex-wrap lg:flex-nowrap items-center gap-4 mb-4">
        {/* Search */}
        <div className="relative flex items-center bg-transparent border border-[#333333] rounded-full w-full sm:w-80">
          <FiSearch className="absolute left-4 w-[24px] text-[#F3F3F3]" />
          <input
            type="text"
            placeholder="Search by Name or Phone"
            className="bg-transparent text-white rounded-md pl-10 py-2.5 leading-normal focus:outline-none w-64"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Calendar Date Picker */}
        <div
          className="relative border border-[#333333] rounded-md"
          ref={datePickerRef}
        >
          <button
            className="flex items-center bg-transparent rounded-md px-2 sm:px-3 py-2 focus:outline-none text-sm sm:text-base"
            onClick={() => setIsDatePickerOpen(!isDatePickerOpen)}
          >
            <BsCalendarDate className="mr-1 sm:mr-2 text-[#FFCA20] text-sm sm:text-base" />
            <span className="hidden sm:inline">
              {startDate && endDate
                ? `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`
                : "Choose Dates"}
            </span>
          </button>

          {isDatePickerOpen && (
            <div className="absolute z-10 mt-2 left-0 bg-[#1e1e1e] rounded-lg p-2 border border-[#333333]">
              <Calendar
                selectRange={true}
                onChange={(update) => {
                  if (update[0] && update[1]) {
                    setDateRange(update);
                    setIsDatePickerOpen(false);
                  }
                }}
                value={[startDate, endDate]}
                calendarType="gregory"
                prev2Label={null}
                next2Label={null}
              />
            </div>
          )}
        </div>

        {/* Download ZIP Button */}
        <button
          onClick={exportToExcelAndZip}
          className="px-4 py-2 rounded-md bg-yellow-400 text-black font-bold"
        >
          Download as ZIP
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-yellow-500"></div>
        </div>
      ) : (
        <div className="bg-[#131313] rounded-lg border border-[#333333] overflow-x-auto">
          <table className="min-w-full border-collapse border border-[#333333] rounded-lg overflow-hidden">
            <thead className="bg-[#1e1e1e] border-t border-[#333333] rounded-lg">
              <tr>
                <th className="border border-[#333333] px-4 py-3">
                  <input
                    type="checkbox"
                    className="h-5 w-5 cursor-pointer"
                    checked={selectedAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Name
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Phone Number
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  WhatsApp Number
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Relation
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  Date Added
                </th>
                <th className="border border-[#333333] px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[#F3F3F3]">
                  City/Location
                </th>
              </tr>
            </thead>
            <tbody className="bg-[#1e1e1e] border border-[#333333]">
              {filteredCustomers.length === 0 ? (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-4 text-center text-[#F3F3F3]"
                  >
                    No customers found
                  </td>
                </tr>
              ) : (
                filteredCustomers.map((customer) => (
                  <tr key={customer?._id}>
                    {/* Checkbox */}
                    <td className="border border-[#333333] px-4 py-3">
                      <input
                        type="checkbox"
                        className="h-5 w-5 cursor-pointer"
                        checked={selectedCustomers.includes(customer?._id)}
                        onChange={() => handleSelectCustomer(customer?._id)}
                      />
                    </td>

                    {/* Full Name */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-white">
                      {customer?.fullName || customer?.name || "N/A"}
                    </td>

                    {/* Phone Number */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.phoneNumber || "N/A"}
                    </td>

                    {/* WhatsApp Number */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.whatsappNumber || "N/A"}
                    </td>

                    {/* Relation */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.relation || "N/A"}
                    </td>

                    {/* Created Date */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-gray-300">
                      {customer?.createdAt
                        ? new Date(customer.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>

                    {/* City */}
                    <td className="border border-[#333333] px-4 py-3 text-sm text-[#F3F3F3]">
                      {customer?.permanentAddress?.city || "N/A"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerManagementContent;
